import { useEffect, useState, type RefObject } from 'react'
import { X } from 'lucide-react'
import type { SkillCard } from '../../lib/skills'

const FEATURED_COUNT = 3

type SkillsRailProps = {
  skills: SkillCard[]
  panelOpen: boolean
  onSelect: (displayMessage: string, skillId: string) => void
  onTogglePanel: () => void
  skillsBtnRef?: RefObject<HTMLButtonElement | null>
  skillsRailRef?: RefObject<HTMLDivElement | null>
  forceVisible?: boolean
}

export const SkillsRail = ({
  skills,
  panelOpen,
  onSelect,
  onTogglePanel,
  skillsBtnRef,
  skillsRailRef,
  forceVisible = false,
}: SkillsRailProps) => {
  const [railOn, setRailOn] = useState(true)
  const featured = skills.slice(0, FEATURED_COUNT)

  useEffect(() => {
    if (forceVisible) setRailOn(true)
  }, [forceVisible])

  if (!railOn) {
    return (
      <div
        ref={skillsRailRef}
        style={{
          maxWidth: 700,
          margin: '0 auto 8px',
          display: 'flex',
        }}
      >
        <button
          type="button"
          onClick={() => setRailOn(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11.5,
            color: 'var(--blue)',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
        >
          <span style={{ color: 'var(--gold)' }}>⚡</span>
          Show Business Skills
        </button>
      </div>
    )
  }

  return (
    <div
      className="no-sb"
      style={{
        maxWidth: 700,
        margin: '0 auto 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        overflowX: 'auto',
        padding: '2px 0',
      }}
    >
      <div
        ref={skillsRailRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          minWidth: 0,
          flex: 1,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 7, whiteSpace: 'nowrap', flexShrink: 0, marginRight: 2 }}>
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              color: 'var(--navy)',
            }}
          >
            Business skills ({skills.length})
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--gold)',
              background: 'rgba(201,168,76,0.14)',
              border: '1px solid rgba(201,168,76,0.32)',
              borderRadius: 4,
              padding: '1px 6px',
              lineHeight: 1.4,
            }}
          >
            ⚡ Guided
          </span>
        </span>
        {featured.map((skill) => (
          <SkillChip key={skill.id} label={skill.title} onClick={() => onSelect(skill.title, skill.id)} />
        ))}
        <button
          ref={skillsBtnRef}
          type="button"
          onClick={onTogglePanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'transparent',
            border: panelOpen ? '1px solid var(--navy)' : '1px dashed rgba(0,0,0,0.16)',
            borderRadius: 100,
            padding: '6px 13px',
            fontSize: 12,
            color: panelOpen ? 'var(--navy)' : 'var(--muted)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'all .18s',
          }}
        >
          {panelOpen ? 'Close' : 'View more'}
        </button>
      </div>
      <button
        type="button"
        aria-label="Hide business skills"
        onClick={() => {
          setRailOn(false)
          if (panelOpen) onTogglePanel()
        }}
        style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: 6,
          color: 'var(--muted2)',
          cursor: 'pointer',
          flexShrink: 0,
          padding: 0,
          background: 'none',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  )
}

const SkillChip = ({ label, onClick }: { label: string; onClick: () => void }) => {
  const [hov, setHov] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        flex: '1 1 0',
        minWidth: 0,
        height: 32,
        boxSizing: 'border-box',
        background: hov ? 'rgba(37,99,235,0.06)' : 'var(--bg)',
        border: `1px solid ${hov ? 'rgba(37,99,235,0.35)' : 'rgba(0,0,0,0.10)'}`,
        borderRadius: 100,
        padding: '0 12px',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1,
        color: hov ? 'var(--blue)' : 'var(--text)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all .18s',
      }}
    >
      <span style={{ color: 'var(--gold)', fontSize: 11, flexShrink: 0, lineHeight: 1 }}>⚡</span>
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {label}
      </span>
    </button>
  )
}
